import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { OrganizationSubscriptionStatusBanner } from '@/bundle/shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { getListData } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { RecipientRequestType, RequestModeType, RequestTypeType } from '@/types/vendorVaultTypes';
import { loadRecipientRequests, loadRequestCounts, loadVendorVaultCatalog, VENDOR_VAULT_QUERY_KEYS } from './api/api';
import { RequestTypeCards } from './ui/RequestTypeCards';
import { RecipientRequestsList } from './ui/RecipientRequestsList';
import { NewRequestModal } from './ui/NewRequestModal/NewRequestModal';
import { RecipientProfileModal } from './ui/RecipientProfileModal/RecipientProfileModal';
import { VendorVaultVerificationRecordModal } from './ui/VendorVaultVerificationRecordModal';
import { CardNewContent } from '@/components-new/CardNew/styles';
import { CardNew } from '@/components-new/CardNew/CardNew';

type ModalStateType =
  | { kind: 'new'; mode: RequestModeType }
  | { kind: 'profile'; request: RecipientRequestType }
  | { kind: 'verification'; request: RecipientRequestType }
  | null;

type ParamsType = { type?: string };

export const VendorVaultPage = () => {
  const queryClient = useQueryClient();
  const searchParams = useGetSearchParams<ParamsType>();
  const { replaceSearchParams } = useReplaceSearchParams<ParamsType>();
  const { isInactiveSubscription, isViewModeSubscription } = useSubscriptionStatus();
  const [modal, setModal] = useState<ModalStateType>(null);

  const requestType = (searchParams.type || 'ach') as RequestTypeType;

  const { data: catalogResponse } = useQuery({
    queryKey: VENDOR_VAULT_QUERY_KEYS.catalog,
    queryFn: loadVendorVaultCatalog,
    staleTime: Infinity,
  });
  const { data: countsResponse } = useQuery({ queryKey: VENDOR_VAULT_QUERY_KEYS.counts, queryFn: loadRequestCounts });
  const listParams = { ...searchParams, request_type: requestType };
  const { data: listResponse, isPending } = useQuery({
    queryKey: [VENDOR_VAULT_QUERY_KEYS.requests, listParams],
    queryFn: () => loadRecipientRequests(listParams),
    placeholderData: keepPreviousData,
  });

  const catalog = catalogResponse?.body;
  const typeMeta = catalog?.request_types[requestType];
  const { list, totalCount, hasPagination } = getListData(listResponse?.body, searchParams);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [VENDOR_VAULT_QUERY_KEYS.requests] });
    queryClient.invalidateQueries({ queryKey: VENDOR_VAULT_QUERY_KEYS.counts });
  };

  const openNew = (mode: RequestModeType) => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.VendorVaultRequestStart);
    setModal({ kind: 'new', mode });
  };

  if (!catalog) return <ContentLayout title='Vendor Vault' />;

  return (
    <ContentLayout
      title='Vendor Vault'
      breadcrumbs='Vendor Vault'
      subtitle='Collect the bank details you need to pay a vendor or recipient. Choose the payment type first; the form asks only for what that type needs.'
      banner={isInactiveSubscription && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        <RequestTypeCards
          catalog={catalog}
          counts={countsResponse?.body ?? {}}
          value={requestType}
          onChange={(value) => replaceSearchParams({ type: value, page: null })}
        />

        <Box marginTop='24px'>
          <CardNew>
            <CardNewContent>
              <Typography variant='subTitleSmall' fontWeight='medium' marginBottom={'4px'}>
                {typeMeta.title}
              </Typography>
              <Typography variant='bodySmall'>{typeMeta.description}</Typography>
            </CardNewContent>
            <CardNewContent justifyContent='center'>
              <Box display='flex' columnGap='14px' marginLeft={'auto'}>
                <ButtonNew
                  size='medium'
                  variant='secondary'
                  disabled={isViewModeSubscription}
                  onClick={() => openNew('request')}
                >
                  Request
                </ButtonNew>
                <ButtonNew size='medium' disabled={isViewModeSubscription} onClick={() => openNew('verify')}>
                  Verify and request
                </ButtonNew>
              </Box>
            </CardNewContent>
          </CardNew>
        </Box>

        <Box marginTop='24px'>
          <RecipientRequestsList
            list={list}
            isLoading={isPending}
            typeLabel={typeMeta.label}
            onViewProfile={(request) => {
              amplitudeService.logEvent(AMPLITUDE_EVENTS.VendorVaultViewProfile);
              setModal({ kind: 'profile', request });
            }}
            onOpenVerification={(request) => setModal({ kind: 'verification', request })}
          />
        </Box>
      </PageBody>

      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}

      {modal?.kind === 'new' && (
        <NewRequestModal
          isOpen
          catalog={catalog}
          requestType={requestType}
          initialMode={modal.mode}
          onClose={() => setModal(null)}
          onSuccess={invalidate}
        />
      )}
      {modal?.kind === 'profile' && (
        <RecipientProfileModal
          isOpen
          requestId={modal.request.id}
          onClose={() => setModal(null)}
          onChanged={invalidate}
          onOpenVerification={() => setModal({ kind: 'verification', request: modal.request })}
        />
      )}
      {modal?.kind === 'verification' && (
        <VendorVaultVerificationRecordModal isOpen request={modal.request} onClose={() => setModal(null)} />
      )}
    </ContentLayout>
  );
};
